import PanelError from '@wandb/weave/common/components/elements/PanelError';
import {TargetBlank} from '@wandb/weave/common/util/links';
import * as _ from 'lodash';
import memoize from 'memoize-one';
import React, {useCallback} from 'react';
// eslint-disable-next-line wandb/no-deprecated-imports
import {Tab} from 'semantic-ui-react';
import useResizeObserver from 'use-resize-observer';

import {getPlotFontSize} from '../../../src/util/plotHelpers/plotFontSize';
import {ChartAggOption} from '../../generated/graphql';
import {useRunsData} from '../../state/runs/hooks';
import docURLs from '../../util/doc_urls';
import {getDefaultLegendTemplate} from '../../util/legend';
import {PanelProps} from '../../util/panelProps';
import {getScalarFromData} from '../../util/plotHelpers/plotHelpers';
import {useStatsigGateWorkspaceConsolidation} from '../../util/rampFeatureFlags';
import * as Run from '../../util/runs';
import LabeledOption from '../elements/LabeledOption';
import {useSingleRunModeContext} from '../MultiRunWorkspace/SingleRunMode';
import {useStablePanelId} from '../Panel/PanelIDContext';
import {DebouncedInputOption} from '../PanelChartOptions';
import {
  PanelChartModal,
  PanelChartPreview,
  PanelChartSettings,
} from '../PanelCommon';
import {
  PanelExpressionOptions,
  parseExpressions,
} from '../PanelExpressionOptions';
import {AggRange} from '../PanelGrouping/AggRange';
import {AggType} from '../PanelGrouping/AggType';
import {usePanelGroupingSettings} from '../PanelRunsLinePlot/RunsLinePlotContext/usePanelGroupingSettings';
import {PanelTitle} from '../PanelTitle/PanelTitle';
import ProjectFieldSelector from '../ProjectFieldSelector';
import ScalarChart from '../vis/ScalarChart';
import {useWorkspaceSettingsColorPalette} from '../WorkspaceDrawer/Settings/colorPalette/ColorPalette';
import {InstrumentedLoader as Loader} from './../../components/utility/InstrumentedLoader';
import {
  defaultTitle,
  getKeysFromMetricStrings,
  getTitleFromConfig,
  isGrouped,
  ScalarChartConfig,
  scalarChartTransformQuery,
} from './common';
import * as S from './PanelScalarChart.styles';

export type PanelScalarChartProps = PanelProps<ScalarChartConfig>;

const PanelScalarChart: React.FC<PanelScalarChartProps> = React.memo(props => {
  const {config, pageQuery, customRunColors, query, loading, configMode} =
    props;
  const {getGroupKeysByRunsetId} = usePanelGroupingSettings();

  // eslint-disable-next-line react-hooks/exhaustive-deps
  const memParseExpressions = useCallback(memoize(parseExpressions), [
    config.expressions,
  ]);

  const parsedExpressions = memParseExpressions(config.expressions);

  const panelQuery = scalarChartTransformQuery(
    pageQuery,
    config,
    parsedExpressions
  );
  const runsDataQuery = useRunsData(panelQuery);
  const {data} = runsDataQuery;

  const elementRef = React.useRef<HTMLDivElement>(null);
  const {height = 1} = useResizeObserver<HTMLDivElement>({
    ref: elementRef,
  });

  const runSets = pageQuery.runSets;

  // We need to memoize this per component instance, rather than globally.
  // Matches logic in PanelRunsLinePlot
  // eslint-disable-next-line react-hooks/exhaustive-deps
  const memGetScalarFromData = useCallback(
    memoize(getScalarFromData, (a: any, b: any) => {
      return a[0] === b[0] && a[1] === b[1] && _.isEqual(a[2], b[2]);
    }),
    [getScalarFromData]
  );

  const isWorkspaceConsolidationEnabled =
    useStatsigGateWorkspaceConsolidation();
  const {isSingleRun} = useSingleRunModeContext();
  const isOldSingleRunWorkspace =
    isSingleRun && !isWorkspaceConsolidationEnabled;

  const defaultLegendTemplate = getDefaultLegendTemplate({
    aggregateMetrics: false,
    isGrouped: isGrouped(pageQuery, config) || false,
    isFullFidelity: false,
    isSmoothed: false,
    legendFields: config.legendFields || [],
    metrics: config.metrics || [],
    isSingleRun,
    type: 'bar',
    useRunsets: runSets != null && runSets.length > 1,
  });
  const colorPaletteOption = useWorkspaceSettingsColorPalette();

  const scalarData = memGetScalarFromData(data.filtered, {
    metricKeys: getKeysFromMetricStrings(config.metrics),
    customRunColors,
    groupAgg: config.groupAgg ?? ChartAggOption.Max,
    groupArea: config.groupArea,
    runSets,
    aggregateCalculations: [],
    legendTemplate: config.legendTemplate || defaultLegendTemplate,
    expressions: parsedExpressions.expressions ?? [],
    entityName: query.entityName,
    projectName: query.projectName,
    groupKeys: getGroupKeysByRunsetId(runSets?.[0]?.id ?? ''),
    colorPaletteOption,
  });

  const renderConfig = () => {
    const {updateConfig} = props;
    if (!data) {
      return <></>;
    }

    const dataTab = (
      <Tab.Pane as="div" className="form-grid">
        <DebouncedInputOption
          label="Title"
          value={config.chartTitle}
          update={chartTitle => updateConfig({chartTitle})}
          placeholder={defaultTitle(config, isSingleRun)}
        />
        <LabeledOption
          label="Metric"
          option={
            <ProjectFieldSelector
              className="with-button"
              query={pageQuery}
              columns={['summary_metrics', 'config']}
              types={['number']}
              defaultKeys={[]}
              multi={false}
              autoPick
              selection
              value={config.metrics?.[0]}
              setValue={value => updateConfig({metrics: [value]})}
              searchByKeyAndText
            />
          }
        />

        {!isOldSingleRunWorkspace && (
          <>
            <AggType
              config={config}
              updateConfig={updateConfig}
              options={{
                allowMedian: true,
                defaultGroupAgg: ChartAggOption.Max,
                includeSum: true,
                disabled: false,
              }}
            />
            <AggRange
              config={config}
              disabled={false}
              includeSamples={false}
              updateConfig={updateConfig}
            />
          </>
        )}

        {isSingleRun && (
          <S.DefineMetricHint>
            If you want to aggregate metrics across a single run, check out{' '}
            <TargetBlank href={docURLs.defineMetric}>
              wandb.define_metric
            </TargetBlank>
            .
          </S.DefineMetricHint>
        )}
      </Tab.Pane>
    );

    const expressionsTab = (
      <Tab.Pane as="div" className="form-grid">
        <PanelExpressionOptions
          type={'scalar'}
          config={config}
          updateConfig={updateConfig}
          availableExpressionVarNames={[]}
          exampleIdentifier={Run.rawConfigKeyDisplayName(
            (config.metrics ? config.metrics[0] : undefined) ?? 'summary:acc'
          )}
        />
      </Tab.Pane>
    );

    const settingsPanes = [
      {
        menuItem: 'Data',
        render: () => dataTab,
      },
      {
        menuItem: 'Expressions',
        render: () => expressionsTab,
      },
    ];

    return (
      <PanelChartModal>
        <PanelChartPreview>{renderGraph()}</PanelChartPreview>
        <PanelChartSettings>
          <Tab
            panes={settingsPanes}
            menu={{
              secondary: true,
              pointing: true,
              className: 'chart-settings-tab-menu',
            }}
          />
        </PanelChartSettings>
      </PanelChartModal>
    );
  };

  const stablePanelId = useStablePanelId();

  const renderGraph = () => {
    let errorMessage;
    let errorLogMessage;

    if (config.metrics == null || config.metrics.length === 0) {
      errorMessage = 'Select a metric to visualize in this chart.';
      errorLogMessage = 'Select a metric to visualize in this chart.';
    } else if (data.filtered.length === 0) {
      errorMessage = (
        <>
          Select runs that logged{' '}
          {config.metrics?.map(Run.rawConfigKeyDisplayName).join(',')} <br />
          to visualize data in this chart.
        </>
      );
      errorLogMessage = `Select runs that logged ${config.metrics?.map(Run.rawConfigKeyDisplayName).join(',')} to visualize data in this chart.`;
    }

    if (loading) {
      return <Loader name="panel-scalar-chart-loader" />;
    }

    // const highlight = _.find(barData, point => point.uniqueId === runHighlight);

    return (
      <div className="chart" ref={elementRef}>
        <PanelTitle
          title={getTitleFromConfig(config)}
          fontSize={getPlotFontSize(config.fontSize ?? 'auto', height)}
        />
        <div
          className="chart-content"
          data-test="chart-content"
          ref={elementRef}>
          {!data.loading && errorMessage != null ? (
            <PanelError
              panelID={stablePanelId}
              panelViewType="Scalar Chart"
              message={errorMessage}
              logErrorMessage={errorLogMessage || 'Scalar chart error occurred'}
            />
          ) : (
            <ScalarChart
              data={scalarData}
              showLegend={
                config.groupAgg == null ||
                config.groupAgg === 'min' ||
                config.groupAgg === 'max'
              }
              groupArea={config.groupArea}
            />
          )}
        </div>
      </div>
    );
  };

  if (configMode) {
    return renderConfig();
  }
  return renderGraph();
});

export default PanelScalarChart;
